/**
 * Canonicalizer Layer for Intake Extraction
 * 
 * The binding translation layer between BreakdownState (the UI truth)
 * and the existing Quentari onboarding / phase-1 schema.
 *
 * Enforces:
 * - Never-Autofill Policy for critical fields
 * - String cleanup (trim, collapse whitespace, empty → null)
 * - Score clamping (1-10)
 * - Enum validation against known registries
 * - Option pair validation (distinct, min length)
 * - Confirmation policy: hard-required fields must be reviewed
 * - Result pattern with errors/warnings
 */

import type { BreakdownState, BreakdownFieldState } from './buildBreakdownState';
import { VALID_DECISION_TYPES, DECISION_SUBTRACKS } from '@/lib/decisionTracks';

// ── Registry (derived from single source of truth) ──

const VALID_TRACKS = VALID_DECISION_TYPES;

const VALID_SUBTRACKS: Record<string, string[]> = Object.fromEntries(
  Object.entries(DECISION_SUBTRACKS).map(([trackId, subs]) => [
    trackId,
    subs.map(s => s.id),
  ])
);

const VALID_PREFERENCES = ['a', 'b', 'undecided'] as const;
const VALID_RISK_LEVELS = ['conservative', 'moderate', 'aggressive'] as const;

// ── Never-Autofill Policy ──
const NEVER_AUTOFILL_FIELDS = [
  'optionA', 'optionB', 'decisionType',
  'impactScore', 'urgencyScore', 'initialPreference',
];

// ── Types ──

export interface CanonicalResult {
  ok: true;
  payload: CanonicalPayload;
  warnings: string[];
}

export interface CanonicalError {
  ok: false;
  errors: Array<{ field: string; message: string }>;
  warnings?: undefined;
  payload?: undefined;
}

export type CanonicalizeResult = CanonicalResult | CanonicalError;

export interface CanonicalPayload {
  decisionType: string;
  decisionSubtype: string | null;
  sessionTitle: string;
  optionA: string;
  optionB: string;
  initialPreference: 'a' | 'b' | 'undecided';
  impactScore: number;
  urgencyScore: number;
  timeHorizonDays: number | null;
  riskToleranceLevel: 'conservative' | 'moderate' | 'aggressive' | null;
  stakesWrongChoiceA: string;
  stakesWrongChoiceB: string;
  stakesInaction: string;
  worstCase: string;
  previousAttempts: string;
  successDefinition: string;
  primaryMetric: string;
}

// ── Helpers ──

function normalizeText(val: unknown): string {
  if (val === null || val === undefined) return '';
  return String(val).trim().replace(/\s+/g, ' ');
}

function normalizeNullableText(val: unknown): string | null {
  const s = normalizeText(val);
  return s.length > 0 ? s : null;
}

function parseScore(val: unknown): number | null {
  const n = Number(val);
  if (isNaN(n) || n === 0) return null;
  return Math.max(1, Math.min(10, Math.round(n)));
}

function parseNullableInt(val: unknown): number | null {
  if (val === null || val === undefined) return null;
  const n = parseInt(String(val), 10);
  return isNaN(n) || n <= 0 ? null : n;
}

function normalizeEnum<T extends string>(val: unknown, allowed: readonly T[]): T | null {
  const s = normalizeText(val).toLowerCase();
  return (allowed as readonly string[]).includes(s) ? (s as T) : null;
}

function isFieldReviewSufficient(field: BreakdownFieldState | undefined): boolean {
  if (!field) return false;
  if (field.detection_status === 'missing') return false;
  const cs = field.confirmation_status;
  return cs === 'accepted' || cs === 'edited' || cs === 'confirmed';
}

function buildFallbackTitle(optionA: string, optionB: string): string {
  const a = optionA.length > 30 ? optionA.slice(0, 30) + '…' : optionA;
  const b = optionB.length > 30 ? optionB.slice(0, 30) + '…' : optionB;
  return `${a} vs. ${b}`;
}

function validateOptionPair(a: string, b: string): string | null {
  if (!a || a.length < 10) return 'Option A muss mindestens 10 Zeichen haben.';
  if (!b || b.length < 10) return 'Option B muss mindestens 10 Zeichen haben.';
  if (a.toLowerCase() === b.toLowerCase()) return 'Option A und B dürfen nicht identisch sein.';
  const wordsA = new Set(a.toLowerCase().split(/\s+/));
  const wordsB = new Set(b.toLowerCase().split(/\s+/));
  const overlap = [...wordsA].filter(w => wordsB.has(w) && w.length > 3);
  if (overlap.length >= Math.min(wordsA.size, wordsB.size) * 0.8) {
    return 'Option A und B müssen klar unterscheidbar sein.';
  }
  return null;
}

function validateTrackSubtype(track: string, subtype: string | null): string | null {
  if (!subtype) return null;
  const validSubs = VALID_SUBTRACKS[track];
  if (!validSubs) return null;
  return validSubs.includes(subtype) ? subtype : null;
}

// ── Main Canonicalizer — accepts BreakdownState ──

export function canonicalizeIntakeExtraction(
  breakdown: BreakdownState,
): CanonicalizeResult {
  const { fields } = breakdown;
  const errors: Array<{ field: string; message: string }> = [];
  const warnings: string[] = [];

  // ── 1. Confirmation Policy for hard-required fields ──
  for (const fieldName of NEVER_AUTOFILL_FIELDS) {
    if (fieldName === 'initialPreference') continue; // soft-required
    const f = fields[fieldName];
    if (!isFieldReviewSufficient(f)) {
      const labels: Record<string, string> = {
        optionA: 'Option A',
        optionB: 'Option B',
        decisionType: 'Entscheidungstyp',
        impactScore: 'Impact-Score',
        urgencyScore: 'Urgency-Score',
      };
      errors.push({
        field: fieldName,
        message: `${labels[fieldName] || fieldName} muss bestätigt oder korrigiert werden.`,
      });
    }
  }

  // ── 2. Extract & validate values ──
  const optionA = normalizeText(fields.optionA?.value);
  const optionB = normalizeText(fields.optionB?.value);

  if (errors.length === 0) {
    const optionError = validateOptionPair(optionA, optionB);
    if (optionError) {
      errors.push({ field: 'optionPair', message: optionError });
    }
  }

  const decisionType = normalizeEnum(fields.decisionType?.value, VALID_TRACKS as unknown as readonly string[]);
  if (!decisionType && errors.every(e => e.field !== 'decisionType')) {
    errors.push({ field: 'decisionType', message: 'Ungültiger Entscheidungstyp.' });
  }

  const impactScore = parseScore(fields.impactScore?.value);
  const urgencyScore = parseScore(fields.urgencyScore?.value);

  if (impactScore === null && errors.every(e => e.field !== 'impactScore')) {
    errors.push({ field: 'impactScore', message: 'Impact-Score muss zwischen 1 und 10 liegen.' });
  }
  if (urgencyScore === null && errors.every(e => e.field !== 'urgencyScore')) {
    errors.push({ field: 'urgencyScore', message: 'Urgency-Score muss zwischen 1 und 10 liegen.' });
  }

  // ── 3. Early return on errors ──
  if (errors.length > 0) {
    return { ok: false, errors };
  }

  // ── 4. Soft fields ──
  const softText = (key: string): string => {
    const f = fields[key];
    if (!f || f.detection_status === 'missing') return '';
    return normalizeText(f.value);
  };

  let decisionSubtype: string | null = normalizeNullableText(fields.decisionSubtype?.value);
  if (decisionSubtype && decisionType) {
    decisionSubtype = validateTrackSubtype(decisionType, decisionSubtype);
  }

  let initialPreference: 'a' | 'b' | 'undecided' = 'undecided';
  const prefField = fields.initialPreference;
  if (prefField && isFieldReviewSufficient(prefField)) {
    const pref = normalizeEnum(prefField.value, VALID_PREFERENCES);
    if (pref) initialPreference = pref;
  } else if (prefField && prefField.detection_status === 'detected') {
    const pref = normalizeEnum(prefField.value, VALID_PREFERENCES);
    if (pref) {
      initialPreference = pref;
      warnings.push('initialPreference wurde nicht explizit bestätigt.');
    }
  }

  const riskToleranceLevel = normalizeEnum(fields.riskToleranceLevel?.value, VALID_RISK_LEVELS);
  const sessionTitle = normalizeNullableText(fields.sessionTitle?.value) || buildFallbackTitle(optionA, optionB);

  // ── 5. Build canonical payload ──
  const payload: CanonicalPayload = {
    decisionType: decisionType!,
    decisionSubtype,
    sessionTitle,
    optionA,
    optionB,
    initialPreference,
    impactScore: impactScore!,
    urgencyScore: urgencyScore!,
    timeHorizonDays: parseNullableInt(fields.timeHorizonDays?.value),
    riskToleranceLevel,
    stakesWrongChoiceA: softText('stakesWrongChoiceA'),
    stakesWrongChoiceB: softText('stakesWrongChoiceB'),
    stakesInaction: softText('stakesInaction'),
    worstCase: softText('worstCase'),
    previousAttempts: softText('previousAttempts'),
    successDefinition: softText('successDefinition'),
    primaryMetric: softText('primaryMetric'),
  };

  return { ok: true, payload, warnings };
}
